import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import { listTypeUses } from "@/infrastructure/type-use/typeUseService";
import type { WarehouseRecord, TypeUse } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}
    >
      {children}
    </div>
  );
}

export function WarehouseFormModal({
  item,
  onSave,
  onClose,
}: {
  item: WarehouseRecord | null;
  onSave: (data: { name: string; code: string; description: string; typeUseId: string }) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(item?.warehouse_name ?? "");
  const [code, setCode] = useState(item?.warehouse_code ?? "");
  const [description, setDescription] = useState(item?.warehouse_description ?? "");
  const [typeUseId, setTypeUseId] = useState(item?.type_use?.id ?? "");
  const [typeUses, setTypeUses] = useState<TypeUse[]>([]);

  useEffect(() => {
    listTypeUses().then(setTypeUses).catch(() => {});
  }, []);

  const isEditing = !!item;
  const isValid = name.trim().length >= 3 && code.trim().length >= 3 && description.trim().length >= 3 && !!typeUseId;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    onSave({ name: name.trim(), code: code.trim(), description: description.trim(), typeUseId });
  };

  return (
    <Overlay onClose={onClose}>
      <div
        style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "480px", display: "flex", flexDirection: "column", gap: "20px" }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {isEditing ? t.warehouses.editWarehouse : t.warehouses.addWarehouse}
        </h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Field label={t.warehouses.nameLabel} value={name} onChange={setName} placeholder={t.warehouses.namePlaceholder} required />
          <Field label={t.warehouses.codeLabel} value={code} onChange={setCode} placeholder={t.warehouses.codePlaceholder} disabled={isEditing} required />
          <Field label={t.warehouses.descriptionLabel} value={description} onChange={setDescription} placeholder={t.warehouses.descriptionPlaceholder} required />

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
              {t.warehouses.typeUseLabel} <span style={{ color: colors.primary }}>*</span>
            </label>
            <select
              value={typeUseId}
              onChange={e => setTypeUseId(e.target.value)}
              style={{
                fontFamily: typography.fontFamily,
                fontSize: typography.size.sm,
                color: typeUseId ? "#111" : "#aaa",
                background: "#fff",
                border: "1.5px solid #e5e5e5",
                borderRadius: "10px",
                padding: "12px 16px",
                outline: "none",
                width: "100%",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              <option value="" disabled>{t.warehouses.typeUsePlaceholder}</option>
              {typeUses.map(tu => (
                <option key={tu.id} value={tu.id}>{tu.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}
            >
              {t.warehouses.cancel}
            </button>
            <button
              type="submit"
              disabled={!isValid}
              style={{
                fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold,
                color: "#fff", background: isValid ? "#111" : "#ccc",
                border: "none", borderRadius: "9px", padding: "10px 20px",
                cursor: isValid ? "pointer" : "not-allowed",
                transition: `background ${transitions.fast}`,
              }}
              onMouseEnter={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }}
              onMouseLeave={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}
            >
              {t.warehouses.save}
            </button>
          </div>
        </form>
      </div>
    </Overlay>
  );
}

function Field({ label, value, onChange, placeholder, disabled = false, required = false }: {
  label: string; value: string; onChange: (v: string) => void; placeholder: string; disabled?: boolean; required?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
        {label} {required && <span style={{ color: colors.primary }}>*</span>}
      </label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: disabled ? "#f5f5f5" : "#fff",
          border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "12px 16px", outline: "none",
          transition: `border-color ${transitions.fast}`, width: "100%", boxSizing: "border-box",
          opacity: disabled ? 0.6 : 1,
        }}
        onFocus={e => { if (!disabled) e.currentTarget.style.borderColor = "#111"; }}
        onBlur={e => (e.currentTarget.style.borderColor = "#e5e5e5")}
      />
    </div>
  );
}
